import Image from 'next/image'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { useBranding } from '@/hooks/use-branding'

interface HomepageUnavailableStateProps {
  className?: string
}

/**
 * Minimal branded placeholder shown when a non-FSDS community has no
 * published homepage data yet (v2 runtime path).
 *
 * This prevents a blank screen while the community homepage is being
 * set up or if the published page was removed.  The component is
 * purely presentational — no data fetching or retry logic.
 */
export default function HomepageUnavailableState({
  className,
}: HomepageUnavailableStateProps) {
  const { siteName, logoUrl } = useBranding()

  return (
    <main
      data-testid="homepage-unavailable-state"
      className={cn(
        'flex min-h-[60vh] flex-col items-center justify-center px-6 py-24 text-center',
        className
      )}
    >
      {logoUrl && (
        <Image
          src={logoUrl}
          alt={siteName}
          width={192}
          height={48}
          unoptimized
          className="mb-8 h-12 w-auto object-contain"
        />
      )}

      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
        This page is being set up
      </h1>

      <p className="mt-3 max-w-md text-base text-gray-500 dark:text-gray-400">
        The homepage for {siteName} is not available yet.
        Please check back soon.
      </p>
    </main>
  )
}
